/**
 * The route contract: the paths, and the one shape every failure takes.
 *
 * Both sides import this, so a renamed route is a compile error on whichever
 * side was not updated rather than a 404 somebody finds by hand.
 */

import type { AuditHit } from "./audit.js";
import type { TemplateGroup } from "./library.js";
import type { Card } from "./cards.js";
import type { DepartmentView } from "./departments.js";
import type { Tailoring } from "./tailor.js";
import type { EmployeeFacts } from "./employees.js";
import type { HandoffReport } from "./handoff.js";
import type { Action, Effect } from "./engine.js";
import type { AccessTime, Assignee, Cancelled } from "./instance.js";
import type { MailEvery, MailWhat } from "./mailing.js";
import type { LevelKind, Member, NoticeTier, StartInput } from "./org.js";
import type { SettingsLevel } from "./settings.js";
import type { SlaStatus } from "./sla.js";
import type { WeekPlan } from "./week.js";
import type { MigrationPlan } from "./migrate.js";
import type { Answer, LifecycleKind, OffboardingUse, Template } from "./template.js";

export const ROUTES = {
  health: "/api/health",
  /** POST an address; a link is mailed to it if it may sign in. */
  authStart: "/api/auth/email/start",
  /**
   * The sign-in screen's button. The mailed link opens /signin, which claims
   * nothing, because mail scanners open links; only this POST redeems it.
   */
  authVerify: "/api/auth/email/verify",
  /**
   * Sign in with Google or Microsoft: a navigation, not a fetch. Start sends
   * the browser to the provider; the provider sends it back to the callback,
   * which ends on `/` signed in or on `/signin?error=` with a key of
   * `SIGN_IN_REFUSALS`.
   */
  googleStart: "/api/auth/google/start",
  googleCallback: "/api/auth/google/callback",
  microsoftStart: "/api/auth/microsoft/start",
  microsoftCallback: "/api/auth/microsoft/callback",
  logout: "/api/auth/logout",
  /** Ends every session the account holds, on every device. */
  logoutEverywhere: "/api/auth/logout-everywhere",
  me: "/api/me",
  /** POST how often the signed-in person's notices are mailed. */
  meMail: "/api/me/mail",
  meMailWhat: "/api/me/mail-what",
  /**
   * The platform admin console: GET the overview, and the writes under it.
   * Every path answers a non-admin with the same 404 as a path that does not
   * exist, signed in or not.
   */
  admin: "/api/admin",
  adminAllowlist: "/api/admin/allowlist",
  /** `/api/admin/accounts/<id>/status` */
  adminAccounts: "/api/admin/accounts",
  adminOrganizations: "/api/admin/organizations",
  /**
   * `GET /api/admin/organizations/<id>`: one organisation's settings and
   * usage, read-only and logged; never its people's work. `POST .../mail`
   * sets its hourly allowance (`{ perHour }`, null to follow the plan).
   */
  /** POST `{ decision: "grant" | "decline" }` to `/api/admin/mail-requests/<id>`. */
  adminMailRequests: "/api/admin/mail-requests",
  /** POST `{ email }` to add a platform admin; DELETE `/<email>` to remove one added here. */
  adminPlatformAdmins: "/api/admin/platform-admins",
  /** Stripe's webhook; there only when `STRIPE_WEBHOOK_SECRET` is set. Signed by Stripe, not by a session. */
  stripeWebhook: "/api/billing/stripe",
  /**
   * POST a `SignupRequest`: a new organisation, the signed-in caller its
   * first admin, on Free. There only while self-serve signup is open, which
   * is while Turnstile is set up (`HealthReport.signup`).
   */
  signup: "/api/signup",
  /** POST, during a key rotation: re-seal every organisation's private values under the new key. */
  adminReseal: "/api/admin/reseal",
  /** `/api/admin/organizations/<id>/name` */
  /**
   * One organisation's work, all under `/api/org/<id>/`: the paths below are
   * relative to that. The worker checks the caller's membership in D1 before
   * the organisation's object is addressed, and addresses it by the id in the
   * path only once that membership says yes.
   */
  org: "/api/org",
} as const;

/** The paths under `/api/org/<id>`, so both sides spell them once. */
export const ORG_PATHS = {
  people: "/people",
  setup: "/setup",
  levels: "/levels",
  staffing: "/staffing",
  instances: "/instances",
  /** POST a `CohortRequest`: several onboardings for the same day, all or none. */
  cohort: "/instances/cohort",
  /** GET the index; `/employees/<id>` is one person and their work. */
  employees: "/employees",
  queue: "/queue",
  notices: "/notices",
  noticesRead: "/notices/read",
  /** Under an instance: POST a new date, or POST to call it off. */
  move: "/move",
  cancel: "/cancel",
  /** Under an employee: POST convert or extend a temp (PRD 9.1). */
  checkpoint: "/checkpoint",
  /** Under /people/<id>: POST when somebody is back from leave (or null). Their own, or an admin's. */
  away: "/away",
  /** Under /people/<id>: POST a `PersonRemoveRequest` to take them out, their work handed on (an admin's). */
  remove: "/remove",
  /** GET who is starting and leaving this week and next, by site. */
  week: "/week",
  /** POST `{ name }`, the organisation's name typed to confirm: closes it, 30 days to export, then deleted (an admin's). */
  close: "/close",
  /** POST: ends a closing organisation's window, nothing deleted (an admin's). */
  reopen: "/reopen",
  /** POST the organisation's own name: its admins'. */
  name: "/name",
  /** GET the starters and the organisation's own; POST a copy of one. `/templates/<id>` is one, POST to save a version. */
  templates: "/templates",
  /** Under an employee: POST `{ reference }` to erase every private value recorded about them (PRD 11.2). */
  erase: "/erase",
  /** POST a `TemplateImportRequest`: each template in the file becomes the organisation's own (an admin's). */
  templateImport: "/templates/import",
  /** Under a template: POST for it as a file (`DataExportResponse`), logged as an export. */
  templateExport: "/export",
  /** Under a template: POST to retire it, or bring it back. */
  retire: "/retire",
  /** Under a template: POST to delete it, which is refused once any work was started from it. */
  deleteTemplate: "/delete",
  /** Under an instance: GET `?to=<template id>` for the plan, POST to move it to that template's current version. */
  migrate: "/migrate",
  /** GET the whole organisation's log, searched (`AuditQuery` as the query string); for whoever `canReadAudit()`. */
  audit: "/audit",
  /** POST an `AuditQuery` for the matching rows as CSV: admins and compliance (`canExportAudit()`), each export logged. */
  auditExport: "/audit/export",
  /** POST `/export/<employees|work|templates>` for the organisation's data (PRD 13.4); each export logged. */
  dataExport: "/export",
  /** POST a `TailorRequest`: the setup wizard's answers make the starters the organisation's own (an admin's). */
  tailor: "/setup/tailor",
  /** POST `{ text }`, a pasted or uploaded list of people: everybody checked, then added (an admin's). */
  peopleBulk: "/people/bulk",
  /** GET every late card across the organisation, and each site's work in progress (PRD 5.4): overseers'. */
  overdue: "/overdue",
  /** GET the organisation's departments with what is in each; POST `{ name }` to add one (an admin's). */
  departments: "/departments",
  /** POST `{ from, to }`: renamed everywhere the name is written, open work included. */
  departmentRename: "/departments/rename",
  /** POST `{ name, archived }`: out of the pickers, or back in. */
  departmentArchive: "/departments/archive",
  /** POST `{ name, level, off }`: where a department is used, from one level down, and where it is switched off. */
  departmentUse: "/departments/use",
  /** GET the organisation's mail pool: its allowance, what it used, and any open request (an admin's). */
  mail: "/mail",
  /** POST a `MailRequestCreate` for a larger hourly allowance, which a platform admin grants or declines. */
  mailRequest: "/mail/request",
  /** GET the organisation's plan and how it pays (`BillingView`, an admin's). */
  billing: "/billing",
  /** POST a `CheckoutRequest`: a Stripe Checkout page for a paid plan, answered as a `BillingRedirect`. */
  billingCheckout: "/billing/checkout",
  /** POST: Stripe's customer portal for the organisation, to change plan, update the card, or cancel. */
  billingPortal: "/billing/portal",
} as const;

export const orgPath = (orgId: string, path: string): string =>
  `${ROUTES.org}/${encodeURIComponent(orgId)}${path}`;

export interface AuthStartRequest {
  email: string;
  /**
   * Turnstile's token, from the widget on the sign-in screen. Needed only
   * while signup is open and only for an address with no account, since that
   * is the mail a stranger could make utuwrk send.
   */
  turnstile?: string;
}

export interface SignupRequest {
  /** The organisation's name. */
  name: string;
  /** Turnstile's token, from the widget on the signup screen. */
  turnstile: string;
}

export interface SignupResponse {
  ok: true;
  /** The new organisation's id, to switch to. */
  id: string;
}

/** An organisation's plan and how it pays, for its admins' Settings. */
/**
 * What an organisation has of what its plan limits, counted the way the
 * worker's plan checks count it (`onboardingProblem()` and its siblings).
 */
export interface PlanUse {
  /** People under management, not counting those who have left or withdrawn. */
  people: number;
  sites: number;
  /** Its own templates, not retired; the starters are always there. */
  templates: number;
  /** Onboardings and offboardings open now. */
  inFlight: number;
}

export interface BillingView {
  ok: true;
  plan: string;
  /** Null when the organisation's work could not be counted; Settings then shows the limits alone. */
  use: PlanUse | null;
  /** Stripe's subscription status, or null when it has never paid through Stripe. */
  status: string | null;
  /** Card payment is set up on this deploy (`STRIPE_SECRET_KEY`): Settings offers a checkout or the portal. */
  available: boolean;
  /** It has a subscription: a change goes through the portal, not another checkout. */
  subscribed: boolean;
  /** It has a Stripe customer, so the portal has somebody to open for (invoices outlive a subscription). */
  portal: boolean;
}

export interface CheckoutRequest {
  plan: string;
  /** Where Stripe sends the admin back to once they have paid: the setup wizard after signup, else Settings. */
  then?: "setup" | "settings";
}

/** A page on Stripe to send the browser to. */
export interface BillingRedirect {
  ok: true;
  url: string;
}

export interface AuthVerifyRequest {
  token: string;
}

/** Who is signed in. Read fresh from D1 on every request, never from the cookie. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  /** On the `ADMIN_EMAILS` secret: the admin console is reachable. Only decides whether the link is drawn. */
  isAdmin: boolean;
}

/** An organisation somebody belongs to, and what they are in it. */
export interface OrgSummary {
  id: string;
  name: string;
  role: MembershipRole;
  department: string;
  /** Its plan (PRD 13.2), so the app can leave out what the plan does not have. The worker decides. */
  plan: string;
  /** When it is deleted, in milliseconds, while it is closing (PRD 13.3); null while it is open. */
  closesAt: number | null;
}

/** `user` is null for a visitor with no session: a fact, not a refusal. */
export interface MeResponse {
  ok: true;
  user: SessionUser | null;
  /** Active memberships only. Empty for a platform admin who is in none. */
  organizations: OrgSummary[];
}

export type AccountStatus = "active" | "disabled";

/** The persona a member holds in one organisation (PRD section 4). */
export type MembershipRole = "admin" | "hr" | "manager" | "member" | "compliance";

export interface AllowlistEntry {
  email: string;
  note: string;
  addedBy: string;
  /** Seconds. */
  createdAt: number;
  /** An account exists for the address: the invitation has been used, or an organisation made one. */
  hasAccount: boolean;
}

export interface AdminAccount {
  id: string;
  email: string;
  name: string;
  status: AccountStatus;
  createdAt: number;
  organizations: { id: string; name: string; role: MembershipRole }[];
}

export interface AdminOrganization {
  id: string;
  name: string;
  plan: string;
  /** Its hourly mail allowance now: the override when set, else its plan's. */
  mailPerHour: number;
  /** The platform admin's figure, or null when it follows the plan. */
  mailOverride: number | null;
  /** Sent from its pool in the last hour. */
  mailLastHour: number;
  /** Stripe's subscription status, when it pays through Stripe; null when its plan is set by hand. */
  billing: string | null;
  /** When it is deleted, in seconds, while it is closing; null while it is open. */
  deleteAfter: number | null;
  createdAt: number;
  members: number;
  /** The addresses holding the admin role in it. */
  admins: string[];
}

/** One row of `admin_actions`: every write the console has made, newest first. */
export interface AdminAction {
  id: number;
  actorEmail: string;
  action: string;
  detail: string;
  createdAt: number;
}

/** Everything the console draws, in one answer; every write answers with it again. */
export interface AdminOverview {
  ok: true;
  allowlist: AllowlistEntry[];
  accounts: AdminAccount[];
  organizations: AdminOrganization[];
  recent: AdminAction[];
  /** Organisations' asks for a larger mail allowance, open ones only, oldest first. */
  mailRequests: AdminMailRequest[];
  platformAdmins: PlatformAdmin[];
  /** `VAULT_KEY_OLD` is set: a key rotation is under way, and the re-seal has work to do. */
  vaultRotating?: boolean;
  /** On the re-seal's answer: what it did, summed across every organisation. */
  resealed?: ResealCounts;
}

export interface ResealCounts {
  organizations: number;
  /** Moved from the key being retired to the current one. */
  resealed: number;
  /** Already under the current key. */
  current: number;
  /** Opened by neither key: lost before this rotation, and lost after it. */
  unreadable: number;
}

export interface AdminMailRequest {
  id: number;
  organizationId: string;
  organizationName: string;
  requestedBy: string;
  /** What they asked for, and what they have. */
  perHour: number;
  current: number;
  reason: string;
  createdAt: number;
  /** Their busiest hour in the last week, and mail held back by a ceiling in it: what the ask rests on. */
  busiestHour: number;
  heldThisWeek: number;
}

export interface PlatformAdmin {
  email: string;
  /** `secret` is `ADMIN_EMAILS`, the break-glass list a deploy changes; `console` was added here and can be removed here. */
  source: "secret" | "console";
  addedBy: string | null;
  createdAt: number | null;
}

export interface AdminPlatformAdminAddRequest {
  email: string;
}

export interface AdminMailAllowanceRequest {
  /** Null follows the plan again. */
  perHour: number | null;
}

export interface AdminMailDecisionRequest {
  decision: "grant" | "decline";
}

/**
 * One organisation as the platform console may see it, to support it: its
 * people's accounts and roles, its structure and templates by name, counts of
 * its work, and its mail. Never a card, a field, an employee's name or a log
 * row; each look is recorded in the console's log.
 */
export interface AdminOrganizationView {
  ok: true;
  organization: AdminOrganization;
  members: { name: string; email: string; role: MembershipRole; department: string; active: boolean }[];
  levels: { kind: string; name: string; parent: string | null }[];
  departments: string[];
  templates: { name: string; kind: string; version: number; retired: boolean }[];
  work: {
    employees: number;
    openOnboardings: number;
    openOffboardings: number;
    finished: number;
    /** Milliseconds; null when nothing has been done yet. */
    lastActivityAt: number | null;
  };
  mail: MailUsage;
}

export interface AdminAllowlistAddRequest {
  email: string;
  note?: string;
}

export interface AdminAccountStatusRequest {
  status: AccountStatus;
}

export interface CloseRequest {
  /** The organisation's name, typed: closing starts the countdown to deleting everything. */
  name: string;
}

export interface OrganizationRenameRequest {
  name: string;
}

export interface OrganizationRenameResponse {
  ok: true;
  name: string;
}

export interface AdminOrganizationPlanRequest {
  plan: string;
}

export interface AdminOrganizationCreateRequest {
  name: string;
  /** Becomes the organisation's first admin, with an account made for it if it has none. */
  adminEmail: string;
  /** Free when absent. */
  plan?: string;
}

/* ------------------------------------------------- one organisation's work -- */

export interface PeopleResponse {
  ok: true;
  people: Member[];
  /** The organisation's departments, archived ones left out: what the pickers offer. */
  departments: string[];
}

export interface TailorRequest {
  tailoring: Tailoring;
}

export interface TailorResponse {
  ok: true;
  /** The organisation's new templates, each with what was changed from its starter. */
  templates: { id: string; name: string; changes: string[] }[];
}

export interface PeopleBulkRequest {
  text: string;
}

export interface MailRequestView {
  id: number;
  perHour: number;
  reason: string;
  status: "open" | "granted" | "declined";
  requestedBy: string;
  createdAt: number;
  decidedAt: number | null;
}

/** An organisation's mail pool, as its admins see it on Settings. */
export interface MailUsage {
  ok: true;
  /** The allowance now, and what its plan alone would give. */
  perHour: number;
  fromPlan: number;
  /** Sent in the last hour; the busiest clock hour in the last seven days; held back by a ceiling in those days. */
  lastHour: number;
  busiestHour: number;
  heldThisWeek: number;
  /** The latest ask for more, whatever came of it; null when there has been none. */
  request: MailRequestView | null;
  /** The most a request may ask for. */
  most: number;
}

export interface MailRequestCreate {
  perHour: number;
  reason?: string;
}

export interface PersonUpdateResponse extends PeopleResponse {
  /** The new address a confirmation link was mailed to, when the address was asked to change. */
  confirming?: string;
}

export interface PeopleBulkResponse extends PeopleResponse {
  added: number;
  /** On the list but already in the organisation, so left as they were. */
  already: number;
}

export interface DepartmentsResponse {
  ok: true;
  departments: DepartmentView[];
  /** Whether the reader may add, rename and archive them (an admin). */
  canEdit: boolean;
}

export interface DepartmentAddRequest {
  name: string;
  /** A company's or a site's id to use it from there down; absent is the whole organisation. */
  level?: string | null;
}

export interface DepartmentUseRequest {
  name: string;
  level: string | null;
  off: string[];
}

export interface DepartmentRenameRequest {
  from: string;
  to: string;
}

export interface DepartmentArchiveRequest {
  name: string;
  archived: boolean;
}

export interface PersonAddRequest {
  email: string;
  name: string;
  role: MembershipRole;
  department: string;
}

/** Any subset; what is absent stays as it was. */
export interface PersonUpdateRequest {
  name?: string;
  /**
   * Their sign-in address, for a name change or a new mailbox. It moves only
   * when the link mailed to the new address is redeemed; until then the old
   * one is still theirs.
   */
  email?: string;
  role?: MembershipRole;
  department?: string;
  active?: boolean;
}

export interface PersonRemoveRequest {
  /** Who takes their unfinished cards and standing assignments; required when they hold any. */
  handTo?: string | null;
}

export interface AwayRequest {
  /** The first day back, or null for here. */
  until: string | null;
}

/** Global, a company, or a site, with only what that level itself sets. */
export interface LevelView {
  id: string;
  kind: LevelKind;
  /** A site's company; null for Global and for a company. */
  parentId: string | null;
  name: string;
  settings: SettingsLevel;
}

/** Who does each department's work at each site by default: site id, then department. */
export type Staffing = Record<string, Record<string, Assignee>>;

export interface SetupResponse {
  ok: true;
  /** Global first, then each company followed by its sites. */
  levels: LevelView[];
  staffing: Staffing;
  /** The organisation's departments, archived ones left out. */
  departments: string[];
  /** Each site's id, then the departments used there: the staffing screen and the start forms offer these. */
  departmentsAt: Record<string, string[]>;
  /** The organisation's own templates, current versions of those not retired, for the start screens (core's `offered()`). */
  templates: Template[];
}

/** A template in the list: a starter, or one of the organisation's own at its current version. */
export interface TemplateSummary {
  id: string;
  name: string;
  kind: LifecycleKind;
  use: OffboardingUse | null;
  version: number;
  /** Where it is offered; empty is every site. */
  sites: string[];
  basedOn: string | null;
  starter: boolean;
  retired: boolean;
  /** Whether work, finished or not, was ever started from it; one that was is retired, never deleted. */
  used: boolean;
  savedAt: number | null;
  /** Who saved the current version, by name as the log has it. */
  savedBy: string | null;
  cards: number;
  /** Unfinished work started from it, and how much of that is on an older version. */
  inFlight: number;
  behind: number;
  /** On one of utuwrk's own: who it is for, and whether it is offered before anybody adopts it. */
  library: { group: TemplateGroup; summary: string; offeredByDefault: boolean } | null;
}

export interface TemplatesResponse {
  ok: true;
  templates: TemplateSummary[];
  /** Whether the reader may change templates (an admin), so the screen offers it. */
  canEdit: boolean;
}

export interface TemplateVersion {
  version: number;
  savedAt: number;
  savedBy: string;
  cards: number;
}

/** Unfinished work started from a template, with the version each is on. */
export interface TemplateWork {
  id: string;
  employeeName: string;
  kind: LifecycleKind;
  anchorDate: string;
  /** The template it was started from: this one, or the starter this one replaces. */
  templateId: string;
  version: number;
}

export interface TemplateResponse {
  ok: true;
  template: Template;
  summary: TemplateSummary;
  versions: TemplateVersion[];
  work: TemplateWork[];
  canEdit: boolean;
}

/** A template file, or Export data's Templates file, read in the browser and sent whole. */
export interface TemplateImportRequest {
  file: unknown;
}

export interface TemplateImportResponse {
  ok: true;
  /** The organisation's new templates, in the file's order. */
  imported: { id: string; name: string }[];
}

/** Copy a starter or one of the organisation's own into a new template of its own. */
export interface TemplateCopyRequest {
  from: string;
}

/**
 * Save a new version. `baseVersion` is the version the editor opened: if
 * somebody saved since, the answer is 409 and nothing is saved, so two admins
 * never overwrite each other (utumap's whole-document saves).
 */
export interface TemplateSaveRequest {
  baseVersion: number;
  template: unknown;
}

export interface TemplateRetireRequest {
  retired: boolean;
}

export interface MigrationResponse {
  ok: true;
  plan: MigrationPlan;
  template: { id: string; name: string; version: number };
}

export interface MigrateRequest {
  /** The template to move to, and the version the person saw the plan for: a newer save since is refused. */
  templateId: string;
  version: number;
  answers?: Record<string, Answer>;
}

export interface LevelCreateRequest {
  kind: "company" | "site";
  /** The company, for a site. */
  parentId?: string;
  name: string;
  settings?: SettingsLevel;
}

export interface LevelSaveRequest {
  name?: string;
  settings: SettingsLevel;
}

export interface StaffingRequest {
  siteId: string;
  department: string;
  primary: string | null;
  backup: string | null;
}

/** What a list of onboardings shows about each, without its cards. */
export interface InstanceSummary {
  id: string;
  kind: LifecycleKind;
  /** Why an offboarding began; `access-change` is somebody staying. Empty for an onboarding. */
  trigger: string;
  templateName: string;
  /** Who it is about, in the Employees index. */
  employeeId: string;
  employeeName: string;
  siteId: string;
  siteName: string;
  anchorDate: string;
  createdAt: number;
  /** Task cards only; notify cards are not work anybody does. */
  total: number;
  finished: number;
  overdue: number;
  /** Cards held by somebody no longer active (PRD 6.4). */
  flagged: number;
  /** The site was under HIPAA when it started, so compliance cards need a reviewer (PRD 11.2). */
  hipaa: boolean;
  closedAt: number | null;
  /** Called off rather than finished; it is closed as well. */
  cancelled: Cancelled | null;
}

export interface InstancesResponse {
  ok: true;
  instances: InstanceSummary[];
}

/** An onboarding or an offboarding. `trigger` and `employeeId` are the offboarding's, and its template must be the one the trigger names. */
export type StartRequest = Omit<
  StartInput,
  "trigger" | "employeeId" | "assignees" | "endDate" | "cutoffTime" | "answers"
> & {
  answers?: Record<string, Answer>;
  trigger?: string;
  employeeId?: string | null;
  assignees?: Record<string, Assignee>;
  endDate?: string | null;
  cutoffTime?: string | null;
};

/**
 * Several onboardings for the same day in one go: everything a start takes
 * but the person, and the people as names (Stage 5's cohorts).
 */
export interface CohortRequest {
  start: Omit<StartRequest, "employeeName" | "employeeId" | "trigger" | "cutoffTime">;
  names: string[];
}

export interface CohortResponse {
  ok: true;
  /** In the order the names were given. */
  started: { id: string; employeeId: string; name: string }[];
  notes: string[];
  unstaffed: StartResponse["unstaffed"];
}

export interface StartResponse {
  ok: true;
  id: string;
  /** What the person starting it should know: deadlines moved up, values a card will not show. */
  notes: string[];
  /** Departments with nobody to do their cards; `open` marks a card that is open now, so nobody has been told. */
  unstaffed: { department: string; cards: { key: string; title: string; open: boolean }[] }[];
}

/** One card as this viewer may see it, with the two things only the server can work out. */
export interface BoardCard {
  card: Card;
  sla: SlaStatus;
  /** Held by somebody no longer active. */
  flagged: boolean;
}

export interface BoardResponse {
  ok: true;
  instance: InstanceSummary;
  /** In dependency order. */
  cards: BoardCard[];
  /** Whether this viewer may skip and reassign: a courtesy, the engine checks again. */
  canDirect: boolean;
  canReadAudit: boolean;
  /** Whether this viewer may move its date or cancel it: whoever may start one. */
  canChange: boolean;
}

/** A new start date (or last day). Every open card's owner is told; the reason goes in the audit log. */
export interface MoveRequest {
  anchorDate: string;
  reason?: string;
}

export interface CancelRequest {
  reason: string;
}

/**
 * Anything `act()` takes, addressed to one card. The answer is the board
 * again, so the screen redraws from what was stored rather than from what it
 * hoped would happen.
 */
export type ActRequest = Action | { type: "poke" } | { type: "comment"; text: string; mentions?: string[] };

export interface QueueCard {
  instanceId: string;
  employeeName: string;
  card: Card;
  sla: SlaStatus;
}

/** PRD 10.2: done (folded away), now, upcoming. */
export interface QueueResponse {
  ok: true;
  done: QueueCard[];
  now: QueueCard[];
  upcoming: QueueCard[];
}

export interface Notice {
  id: number;
  at: number;
  instanceId: string;
  /** Empty for a notice about a person rather than a card. */
  taskKey: string;
  why: NoticeWhy;
  /** Set when the notice is about a person: it opens their page. */
  employeeId: string | null;
  urgent: boolean;
  text: string;
  read: boolean;
  /** Whose workflow it is about: the person the cards are for. Null when its workflow is gone. */
  employeeName: string | null;
  /** Folds cards of several people's workflows, so it has no one person. */
  several: boolean;
  /** Asks something of the reader, or only tells them something (`noticeTier()`). */
  tier: NoticeTier;
  /** Its cards are all finished: drawn as done, and no longer counted as needing the reader. */
  resolved: boolean;
}

/** A card's reasons (`Effect["why"]`), and the one about a person: a temp's contract nearing its end. */
export type NoticeWhy = Effect["why"] | "contract-ending" | "week-ahead";

/** One site's weeks: who starts and who leaves, from the calendar at that site. */
export interface SiteWeek {
  siteId: string;
  siteName: string;
  /** The day it is at the site. */
  today: string;
  thisWeek: WeekPlan;
  nextWeek: WeekPlan;
}

export interface WeekResponse {
  ok: true;
  sites: SiteWeek[];
}

export interface NoticesResponse {
  ok: true;
  notices: Notice[];
  /** Unread notices that need the reader: the badge. The rest are in the list, unmarked by it. */
  unread: number;
  /** How often this person's notices are mailed: the account's setting, shown beside the list. */
  mailEvery: MailEvery;
  mailWhat: MailWhat;
}

export interface MailEveryRequest {
  every: MailEvery;
}

export interface MailWhatRequest {
  what: MailWhat;
}

export interface MailWhatResponse {
  ok: true;
  mailWhat: MailWhat;
}

export interface MailEveryResponse {
  ok: true;
  mailEvery: MailEvery;
}

/**
 * One row of the Employees index: `employeeFacts()` worked out from their
 * work, with the names the screen needs resolved. Nothing here was typed
 * about the person except their name and a temp's end date.
 */
export interface EmployeeSummary extends Omit<EmployeeFacts, "siteId"> {
  id: string;
  name: string;
  siteId: string;
  siteName: string;
  managerName: string | null;
  /** A temp's last contracted day. */
  endDate: string | null;
  /** Cards on their work somebody can act on now, across all of it. */
  openCards: number;
  createdAt: number;
}

export interface EmployeesResponse {
  ok: true;
  employees: EmployeeSummary[];
}

/** One person: their row, every onboarding and offboarding about them, and the cards open on it now. */
export interface EmployeeResponse {
  ok: true;
  employee: EmployeeSummary;
  /** Newest first. */
  work: InstanceSummary[];
  /** As `cardFor()` shows each to this viewer, soonest due first. */
  openCards: QueueCard[];
  canReadAudit: boolean;
  /** Whether this viewer may decide a temp's checkpoint: whoever may start work. */
  canChange: boolean;
  /** Whether this viewer may erase their private values (`canErase()`). */
  canErase: boolean;
  /** Private values recorded across their work and not yet erased. */
  privateValues: number;
}

export interface EraseRequest {
  /** The request's own number, which goes into every marker and the log. */
  reference: string;
}

/** A late card, with where it is and whose work it is part of. */
export interface OverdueCard extends QueueCard {
  kind: LifecycleKind;
  employeeId: string;
  siteId: string;
  siteName: string;
  status: "overdue" | "critical";
  /** How long past due, in milliseconds, when the answer was made. */
  lateBy: number;
  /** Held by somebody no longer active. */
  flagged: boolean;
}

/** One site's work in progress at a glance. */
export interface SiteLoad {
  siteId: string;
  siteName: string;
  onboarding: number;
  offboarding: number;
  /** Late cards on that work. */
  late: number;
}

/** PRD 5.4's overdue dashboard and in-progress view, across every site. */
export interface OverdueResponse {
  ok: true;
  /** Latest first. */
  cards: OverdueCard[];
  sites: SiteLoad[];
}

export interface NoticesReadRequest {
  /** Everything up to and including this id is marked read. */
  upTo: number;
}

export interface AuditEntry {
  seq: number;
  at: number;
  actorId: string;
  actorName: string;
  action: string;
  taskKey: string | null;
  taskTitle: string | null;
  from: string | null;
  to: string | null;
  note: string | null;
}

export interface AuditResponse {
  ok: true;
  entries: AuditEntry[];
  /** The hash chain recomputed from the first row (decisions.md, "The audit log is append-only"). */
  intact: boolean;
  /** The Stage 1 gate's number for this onboarding (PRD 3.2), worked out from these entries. */
  handoffs: HandoffReport;
  /** Each card that revokes access: when it was asked for and when it was done (`accessTimes()`). Empty on an onboarding. */
  access: AccessTime[];
  /** The site's zone, which the access times are read in. */
  timeZone: string;
}

/** A page of the organisation's log, newest first. */
export interface AuditSearchResponse {
  ok: true;
  hits: AuditHit[];
  /** Pass as `before` for the next page; null when this is the last. */
  more: number | null;
  /** The hash chain recomputed from the first row. */
  intact: boolean;
  sites: { id: string; name: string }[];
  departments: string[];
  canExport: boolean;
  /** The latest head of the log written outside utuwrk's own storage, which the check above also holds it to. */
  anchor: { seq: number; hash: string; at: number } | null;
}

/** A file of the organisation's data, logged as an export before it was handed over. */
export interface DataExportResponse {
  ok: true;
  filename: string;
  /** What to save it as: CSV or JSON. */
  type: string;
  text: string;
  rows: number;
}

export interface DataExportRequest {
  /** The work history with private values in it: a compliance officer's alone. */
  withPrivate?: boolean;
}

export interface AuditExportResponse {
  ok: true;
  /** A name to save it under. */
  filename: string;
  csv: string;
  rows: number;
}

/**
 * Every refusal. `reason` is a sentence a person could read off the screen,
 * written next to the check that produced it; the client never maps a status
 * code onto words. `missing` appears only on a 503 from a half-finished deploy.
 */
export interface ApiError {
  ok: false;
  reason: string;
  missing?: string[];
}

export interface HealthReport {
  ok: true;
  /** Everything an authenticated route needs: configuration *and* an applied schema. */
  ready: boolean;
  /** Sign-in mail can go out. Allowed to be false while `ready` is true. */
  email: boolean;
  /** Which sender it goes through, when it can: Amazon SES, or Cloudflare Email Service. */
  mailVia?: "ses" | "cloudflare";
  /** Private (PII) values can be stored: the encryption key is set. Allowed to be false while `ready` is true. */
  vault: boolean;
  /** Which "Continue with" buttons the sign-in screen shows: only a provider whose secrets are all set. */
  google: boolean;
  microsoft: boolean;
  /**
   * Turnstile's site key while self-serve signup is open; null while utuwrk
   * is invite-only. The sign-in and signup screens draw the widget from it.
   */
  signup: string | null;
  missing?: string[];
}

/**
 * The sentences a Google or Microsoft sign-in can end on. The callback is a
 * navigation, so it cannot answer `{ ok: false, reason }`: it redirects to
 * `/signin?error=<key>`, and the screen shows the sentence for that key. A key
 * rather than the sentence itself, so nobody can craft a link that puts their
 * own words on utuwrk's sign-in screen.
 */
export const SIGN_IN_REFUSALS = {
  "oauth-failed": "That sign-in did not finish. Try again, or use an emailed link.",
  "oauth-unavailable": "That way of signing in is not set up here. Use an emailed link.",
  "oauth-unverified":
    "Microsoft did not confirm the email address on that account, so utuwrk cannot tell whose it is. Use an emailed link instead.",
  "not-invited": "That address is not on the invite list.",
  disabled: "That account has been disabled.",
  "too-many": "Too many tries from here. Wait ten minutes and try again.",
} as const;

export type SignInRefusal = keyof typeof SIGN_IN_REFUSALS;
